import { TypeChecker } from '@attain-apps/type-guards'

import * as common from '../common'

import type { DocumentNumber } from './document-number'
import type * as payment from './payment'
import type { ProjectId } from './project-id'
import { normalizeDate, normalizeText, parseNumber } from './sheet-cells'
import type { Subaccount } from './subaccount'

/**
 * The BCH cash draw workbook: its tab names, how it is recognized among
 * uploads, and the rows of the tabs the BCH agents read and write. Each agent
 * builds on this one description instead of keeping its own.
 */
export interface CashDraw {
  readonly pacct: readonly CashDraw.PacctRow[]
  readonly pmsDownload: readonly CashDraw.PmsDownloadRow[]
  readonly pmsUpload: readonly CashDraw.PmsUploadRow[]
}
export namespace CashDraw {
  /** The workbook's tab names. */
  export const NAMES = {
    loc: 'LOC',
    pacct: 'PACCT',
    pacctPivot: 'PACCT Pivot',
    pmsDownload: 'PMS Download',
    pmsUpload: 'PMS Upload',
  } as const

  /**
   * How a cash draw workbook is recognized among a message's uploads, shared
   * by every BCH agent that reads one.
   */
  export const FILE_ROLE = {
    label: 'cash draw workbook',
    words: ['cash', 'draw'],
  } as const

  /**
   * Cash draw workbook worksheets needed by the BCH workflow.
   */
  export interface Worksheets {
    readonly pacct: readonly common.SheetRow[]
    readonly pmsDownload: readonly common.SheetRow[]
    /**
     * Optional: only the `PMS Upload` writer reads these rows, so a draw file
     * without the tab still supports building the justifications workbook.
     */
    readonly pmsUpload?: readonly common.SheetRow[]
  }
  export namespace Worksheets {
    export const isWorksheets: TypeChecker<Worksheets> =
      TypeChecker.Factory.object({
        pacct: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
        pmsDownload: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
        pmsUpload: TypeChecker.Factory.optional(
          TypeChecker.Factory.list(common.SheetRow.isSheetRow),
        ),
      }).usingAlias('CashDraw.Worksheets')
  }

  export const isCashDraw: TypeChecker<CashDraw> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object({
        pacct: TypeChecker.Factory.list(PacctRow.isPacctRow),
        pmsDownload: TypeChecker.Factory.list(PmsDownloadRow.isPmsDownloadRow),
        pmsUpload: TypeChecker.Factory.list(PmsUploadRow.isPmsUploadRow),
      }),
  )

  /**
   * Builds the normalized cash draw section from source worksheet rows.
   */
  export function fromWorksheets(sheets: Readonly<Worksheets>): CashDraw {
    return {
      pacct: PacctRow.fromSheet(sheets.pacct),
      pmsDownload: PmsDownloadRow.fromSheet(sheets.pmsDownload),
      pmsUpload: sheets.pmsUpload
        ? PmsUploadRow.fromSheet(sheets.pmsUpload)
        : [],
    }
  }

  export interface PacctRow {
    readonly projectId: ProjectId
    readonly documentNumber?: DocumentNumber
    readonly ohRate?: number
    readonly projectEndDate?: string
    readonly effectiveDate?: string
    readonly raw?: Record<string, unknown>
  }
  export namespace PacctRow {
    /** PACCT column names. */
    export const HEADERS = {
      documentNumber: 'Document #',
      effectiveDate: 'Effective Date',
      /** The `chglr008` column, used when `Document #` is blank. */
      glDocumentNumber: 'Document Number',
      ohRate: 'OH Rate',
      projectEndDate: 'Project End Date',
      projectId: 'ProjectID',
    } as const

    export const isPacctRow: TypeChecker<PacctRow> = TypeChecker.Factory.object(
      {
        documentNumber: TypeChecker.Factory.optional(TypeChecker.isString),
        effectiveDate: TypeChecker.Factory.optional(TypeChecker.isString),
        ohRate: TypeChecker.Factory.optional(TypeChecker.isNumber),
        projectEndDate: TypeChecker.Factory.optional(TypeChecker.isString),
        projectId: TypeChecker.isString,
        raw: TypeChecker.Factory.optional(common.SheetRow.isRecord),
      },
    )

    /**
     * Parses the cash draw PACCT worksheet.
     */
    export function fromSheet(rows: readonly common.SheetRow[]): PacctRow[] {
      const { body, columns, header } = common.SheetRow.parseTable(rows, {
        documentNumber: [HEADERS.documentNumber],
        ohRate: [HEADERS.ohRate],
        projectId: [HEADERS.projectId],
      })
      const documentNumberCol = common.SheetRow.findOptionalColumn(header, [
        HEADERS.glDocumentNumber,
      ])
      const projectEndDateCol = common.SheetRow.findOptionalColumn(header, [
        HEADERS.projectEndDate,
      ])
      const effectiveDateCol = common.SheetRow.findOptionalColumn(header, [
        HEADERS.effectiveDate,
      ])

      return body
        .map(row => {
          const projectId = normalizeText(row[columns.projectId])
          if (!projectId) {
            return undefined
          }
          return {
            documentNumber:
              normalizeText(row[columns.documentNumber]) ??
              common.SheetRow.valueAt(row, documentNumberCol, normalizeText),
            effectiveDate: common.SheetRow.valueAt(
              row,
              effectiveDateCol,
              normalizeDate,
            ),
            ohRate: common.SheetRow.parseOptionalNumber(row[columns.ohRate]),
            projectEndDate: common.SheetRow.valueAt(
              row,
              projectEndDateCol,
              normalizeDate,
            ),
            projectId,
            raw: common.SheetRow.toRecord(header, row),
          } satisfies PacctRow
        })
        .filter(TypeChecker.isNotNullish.raw)
    }
  }

  export interface PacctPivotRow {
    readonly documentNumber: DocumentNumber
    readonly drawdownAmount: number
    readonly raw?: Record<string, unknown>
  }
  export namespace PacctPivotRow {
    /**
     * PACCT Pivot column names, and its totals label. The PACCT agent writes
     * this tab; Defend to Spend no longer reads it, since its drawdown comes
     * from PMS Download.
     */
    export const HEADERS = {
      documentNumber: 'Row Labels',
      drawdownAmount: 'Sum of Drawdown Amount',
      grandTotal: 'Grand Total',
    } as const
  }

  export interface PmsDownloadRow {
    readonly documentNumber: DocumentNumber
    /** `Subacct Amt Requested $`: the drawdown requested for the document. */
    readonly drawdownAmount: number
    readonly raw?: Record<string, unknown>
  }
  export namespace PmsDownloadRow {
    /** PMS Download column names. */
    export const HEADERS = {
      documentNumber: 'Subacct Code',
      drawdownAmount: 'Subacct Amt Requested $',
    } as const

    export const isPmsDownloadRow: TypeChecker<PmsDownloadRow> =
      TypeChecker.Factory.object({
        documentNumber: TypeChecker.isString,
        drawdownAmount: TypeChecker.isNumber,
        raw: TypeChecker.Factory.optional(common.SheetRow.isRecord),
      })

    /**
     * Parses the cash draw PMS Download worksheet. Rows without a
     * `Subacct Code`, such as the totals under the table, are skipped.
     */
    export function fromSheet(
      rows: readonly common.SheetRow[],
    ): PmsDownloadRow[] {
      const { body, columns, header } = common.SheetRow.parseTable(rows, {
        documentNumber: [HEADERS.documentNumber],
        drawdownAmount: [HEADERS.drawdownAmount],
      })

      return body
        .map(row => {
          const documentNumber = normalizeText(row[columns.documentNumber])
          if (!documentNumber) {
            return undefined
          }
          return {
            documentNumber,
            drawdownAmount: parseNumber(row[columns.drawdownAmount]),
            raw: common.SheetRow.toRecord(header, row),
          } satisfies PmsDownloadRow
        })
        .filter(TypeChecker.isNotNullish.raw)
    }
  }

  export interface PmsUploadRow {
    readonly rowIndex: number
    readonly paymentType?: payment.Type
    readonly subacctCode?: Subaccount.Code
    readonly paymentJustification?: string
    readonly raw?: Record<string, unknown>
  }
  export namespace PmsUploadRow {
    /**
     * PMS Upload column names used by parsing and workbook population.
     */
    export const HEADERS = {
      paymentJustification: 'Payment Justification (Maximum 1000 Characters)',
      paymentType: 'Payment Type',
      subacctCode: 'Subacct Code',
    } as const

    export const isPmsUploadRow: TypeChecker<PmsUploadRow> =
      TypeChecker.Factory.object({
        paymentJustification: TypeChecker.Factory.optional(
          TypeChecker.isString,
        ),
        paymentType: TypeChecker.Factory.optional(TypeChecker.isString),
        raw: TypeChecker.Factory.optional(common.SheetRow.isRecord),
        rowIndex: TypeChecker.isNumber,
        subacctCode: TypeChecker.Factory.optional(TypeChecker.isString),
      })

    /**
     * Parses the cash draw PMS Upload worksheet.
     */
    export function fromSheet(
      rows: readonly common.SheetRow[],
    ): PmsUploadRow[] {
      const { body, columns, header, preamble } = common.SheetRow.parseTable(
        rows,
        {
          paymentType: [HEADERS.paymentType],
          subacctCode: [HEADERS.subacctCode],
        },
      )
      const paymentJustificationCol = common.SheetRow.findOptionalColumn(
        header,
        [HEADERS.paymentJustification],
      )

      return body
        .map((row, index) => {
          const paymentType = normalizeText(row[columns.paymentType])
          const subacctCode = normalizeText(row[columns.subacctCode])
          if (!paymentType && !subacctCode) {
            return undefined
          }
          return {
            paymentJustification: common.SheetRow.valueAt(
              row,
              paymentJustificationCol,
              normalizeText,
            ),
            paymentType,
            raw: common.SheetRow.toRecord(header, row),
            // Rows are zero-based here; PMS upload row ids need the one-based
            // Excel row number, counting the preamble and the header row.
            rowIndex: preamble.length + index + 2,
            subacctCode,
          } satisfies PmsUploadRow
        })
        .filter(TypeChecker.isNotNullish.raw)
    }
  }
}
